/**
 * AssistantLauncher — the always-visible pill at bottom-right.
 *
 * Two modes:
 *   • Desktop (≥640px): pill with sparkle + "Ask AI" + ⌘K badge.
 *     Looks like Linear / Raycast — power-tool, not chatbot.
 *   • Mobile (<640px):  compact pill with sparkle + "Ask AI"
 *     (no ⌘K — keyboard shortcuts are irrelevant on phones).
 *     Respects iOS safe-area-inset-bottom so it sits above the home
 *     indicator instead of being half-hidden behind it.
 *
 * Hidden when the palette is open so it doesn't compete visually.
 */
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

import { useAssistant } from './AssistantContext';

export function AssistantLauncher() {
  const { open, setOpen } = useAssistant();
  if (open) return null;

  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return (
    <motion.button
      type="button"
      onClick={() => setOpen(true)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.18 }}
      aria-label="Open AI assistant"
      // Inline style for safe-area: Tailwind can't express env() in v3
      // without a plugin. max() falls back to 1.25rem when the inset is 0.
      style={{
        bottom: 'max(1.25rem, env(safe-area-inset-bottom))',
        right: 'max(1.25rem, env(safe-area-inset-right))',
      }}
      className={[
        'fixed z-40',
        // Shape — slightly bigger touch target on mobile (44px+ height)
        'group flex items-center gap-2 rounded-full',
        'pl-3 pr-3 py-2.5 sm:pl-4 sm:pr-2.5 sm:py-2.5',
        // Glass surface + violet glow (signals "AI", not "chat")
        'bg-white/90 dark:bg-surface-900/90 backdrop-blur-xl',
        'border border-violet-200/60 dark:border-violet-500/30',
        'shadow-lg shadow-violet-500/25 dark:shadow-violet-500/15',
        'hover:shadow-xl hover:shadow-violet-500/30',
        'active:scale-[0.97]',
        'transition-shadow',
        // Text
        'text-sm font-semibold text-surface-700 dark:text-surface-200',
        // Don't let the launcher get focused on accidental taps
        'select-none',
      ].join(' ')}
    >
      {/* Gradient sparkle circle — visual differentiator from chat bubbles */}
      <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 text-white shadow-inner">
        <Sparkles className="h-4 w-4" strokeWidth={2.5} />
      </span>
      {/* Label always visible — discoverable on mobile, not cryptic. */}
      <span>Ask AI</span>
      {/* Keyboard hint only on desktop where it's meaningful. */}
      <kbd
        className={[
          'hidden sm:inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5',
          'text-[10px] font-mono font-bold',
          'bg-surface-100 dark:bg-surface-800',
          'text-surface-500 dark:text-surface-400',
          'border border-surface-200 dark:border-surface-700',
        ].join(' ')}
        aria-hidden="true"
      >
        {isMac ? '⌘' : 'Ctrl'}K
      </kbd>
    </motion.button>
  );
}
